import { useState, type FormEvent } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { AdminPersonView, Role, WorkspaceAdminView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Dialog } from "../../../ui/Dialog";
import { TextField } from "../../../ui/TextField";
import { showToast } from "../../../ui/toastStore";
import { checkFirstPassword } from "./accountChecks";
import { CheckList } from "./CheckList";
import { usePeopleCache } from "./useAdminPeople";

/** Set a first password for an account that cannot sign in, or that forgot its password. Every reset is recorded. */
export function ResetPasswordDialog({ person, onClose }: { person: AdminPersonView; onClose: () => void }) {
  const reset = useApiCommand("reset_password");
  const cache = usePeopleCache();
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const result = checkFirstPassword(password, person.username);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!result.ok) return;
    setProblem(null);
    try {
      const reply = await reset.mutateAsync({ id: person.id, first_password: password });
      cache.replace(reply.data);
      setPassword("");
      showToast({ text: `A first password is set for ${person.display_name}. They must change it when they sign in. The reset is recorded.` });
      onClose();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Dialog
      closeOnBackdrop={false}
      open
      title={`Set a first password for ${person.display_name}`}
      onClose={reset.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={reset.isPending} disabled={!result.ok} onClick={() => void submit()}>
            Set the first password
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(event) => void submit(event)} autoComplete="off">
        <p className="muted">It works once, runs out after a set time and must be changed at the next sign-in. Every reset is recorded, and the person is told at their next sign-in.</p>
        <TextField label="First password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" data-autofocus />
        <CheckList checks={result.checks} started={password.length > 0} />
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export const ROLE_CHOICES: Array<{ role: Role; label: string }> = [
  { role: "engineer", label: "Engineer" },
  { role: "sme", label: "SME (subject matter expert)" },
];

/** Two role boxes. Both may be ticked: a person with both roles uses both sides, and their own work is marked in the SME inbox. */
export function RoleBoxes({ roles, onChange, disabled }: { roles: readonly Role[]; onChange: (roles: Role[]) => void; disabled?: boolean }) {
  return (
    <div role="group" aria-label="Roles" className="ra-roles">
      {ROLE_CHOICES.map(({ role, label }) => (
        <label key={role} className="ra-checkbox">
          <input
            type="checkbox"
            checked={roles.includes(role)}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked ? [...roles.filter((r) => r !== role), role] : roles.filter((r) => r !== role))}
          />{" "}
          {label}
        </label>
      ))}
    </div>
  );
}

/** Add a person to a discipline with one role or both. */
export function GrantDialog({ person, workspaces, onClose }: { person: AdminPersonView; workspaces: readonly WorkspaceAdminView[]; onClose: () => void }) {
  const grant = useApiCommand("grant_membership");
  const cache = usePeopleCache();
  const joined = new Set(person.memberships.map((membership) => membership.workspace_code));
  const choices = workspaces.filter((workspace) => workspace.status === "open" && !joined.has(workspace.code));
  const [code, setCode] = useState(choices[0]?.code ?? "");
  const [roles, setRoles] = useState<Role[]>(["engineer"]);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async () => {
    if (!code || roles.length === 0) return;
    setProblem(null);
    try {
      const reply = await grant.mutateAsync({ id: person.id, workspace_code: code, roles });
      cache.replace(reply.data);
      showToast({ text: `${person.display_name} is now in ${code}.` });
      onClose();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Dialog
      closeOnBackdrop={false}
      open
      title={`Add ${person.display_name} to a discipline`}
      onClose={grant.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={grant.isPending} disabled={!code || roles.length === 0} onClick={() => void submit()}>
            Add to the discipline
          </Button>
        </>
      }
    >
      <div className="stack">
        {choices.length === 0 ? (
          <p className="muted">There is no open discipline left that {person.display_name} is not in. Add a discipline first.</p>
        ) : (
          <>
            <div className="field">
              <label htmlFor="ra-grant-code" className="field__label">
                Discipline
              </label>
              <select id="ra-grant-code" className="field__input" value={code} onChange={(event) => setCode(event.target.value)} data-autofocus>
                {choices.map((workspace) => (
                  <option key={workspace.code} value={workspace.code}>
                    {workspace.display_name} ({workspace.code})
                  </option>
                ))}
              </select>
            </div>
            <RoleBoxes roles={roles} onChange={setRoles} />
            {roles.length === 0 && <p className="field__error">Choose at least one role.</p>}
          </>
        )}
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
      </div>
    </Dialog>
  );
}
